import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Initial state
const initialState = {
  // Farm data
  zones: [],
  selectedZone: null,
  zoneMetrics: {},
  
  // Weather data
  currentWeather: null,
  forecast: [],
  historicalWeather: [],
  
  // Agent data
  recommendations: [],
  tasks: [],
  
  // UI state
  isLoading: false,
  error: null,
  notifications: [],
  
  // User preferences
  userPreferences: {
    theme: 'light',
    dashboardLayout: 'default',
    metricUnits: 'metric', // or 'imperial'
  }
};

// Action types
const ActionTypes = {
  SET_ZONES: 'SET_ZONES',
  SET_SELECTED_ZONE: 'SET_SELECTED_ZONE',
  SET_ZONE_METRICS: 'SET_ZONE_METRICS',
  SET_CURRENT_WEATHER: 'SET_CURRENT_WEATHER',
  SET_FORECAST: 'SET_FORECAST',
  SET_HISTORICAL_WEATHER: 'SET_HISTORICAL_WEATHER',
  SET_RECOMMENDATIONS: 'SET_RECOMMENDATIONS',
  SET_TASKS: 'SET_TASKS',
  ADD_TASK: 'ADD_TASK',
  UPDATE_TASK: 'UPDATE_TASK',
  DELETE_TASK: 'DELETE_TASK',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  UPDATE_USER_PREFERENCES: 'UPDATE_USER_PREFERENCES',
};

// Reducer function
const reducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_ZONES:
      return { ...state, zones: action.payload };
    
    case ActionTypes.SET_SELECTED_ZONE:
      return { ...state, selectedZone: action.payload };
    
    case ActionTypes.SET_ZONE_METRICS:
      return { 
        ...state, 
        zoneMetrics: { 
          ...state.zoneMetrics, 
          [action.payload.zoneId]: action.payload.metrics 
        } 
      };
    
    case ActionTypes.SET_CURRENT_WEATHER:
      return { ...state, currentWeather: action.payload };
    
    case ActionTypes.SET_FORECAST:
      return { ...state, forecast: action.payload };
    
    case ActionTypes.SET_HISTORICAL_WEATHER:
      return { ...state, historicalWeather: action.payload };
    
    case ActionTypes.SET_RECOMMENDATIONS:
      return { ...state, recommendations: action.payload };
    
    case ActionTypes.SET_TASKS:
      return { ...state, tasks: action.payload };
    
    case ActionTypes.ADD_TASK:
      return { ...state, tasks: [...state.tasks, action.payload] };
    
    case ActionTypes.UPDATE_TASK:
      return { 
        ...state, 
        tasks: state.tasks.map(task => 
          task.id === action.payload.id ? { ...task, ...action.payload } : task
        ) 
      };
    
    case ActionTypes.DELETE_TASK:
      return { 
        ...state, 
        tasks: state.tasks.filter(task => task.id !== action.payload) 
      };
    
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload };
    
    case ActionTypes.CLEAR_ERROR:
      return { ...state, error: null };
    
    case ActionTypes.ADD_NOTIFICATION:
      return { 
        ...state, 
        notifications: [...state.notifications, { id: Date.now(), ...action.payload }] 
      };
    
    case ActionTypes.REMOVE_NOTIFICATION:
      return { 
        ...state, 
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        ) 
      };
    
    case ActionTypes.UPDATE_USER_PREFERENCES:
      return { 
        ...state, 
        userPreferences: { ...state.userPreferences, ...action.payload } 
      };
    
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Context provider component
const AppContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  
  // Load saved user preferences from localStorage on mount
  useEffect(() => {
    try {
      const savedPreferences = localStorage.getItem('userPreferences');
      if (savedPreferences) {
        dispatch({ 
          type: ActionTypes.UPDATE_USER_PREFERENCES, 
          payload: JSON.parse(savedPreferences) 
        });
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  }, []);
  
  // Save user preferences to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem('userPreferences', JSON.stringify(state.userPreferences));
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  }, [state.userPreferences]);
  
  // Actions
  const actions = {
    setZones: (zones) => dispatch({ type: ActionTypes.SET_ZONES, payload: zones }),
    
    setSelectedZone: (zoneId) => dispatch({ 
      type: ActionTypes.SET_SELECTED_ZONE, 
      payload: zoneId 
    }),
    
    setZoneMetrics: (zoneId, metrics) => dispatch({ 
      type: ActionTypes.SET_ZONE_METRICS, 
      payload: { zoneId, metrics } 
    }),
    
    setCurrentWeather: (weather) => dispatch({ 
      type: ActionTypes.SET_CURRENT_WEATHER, 
      payload: weather 
    }),
    
    setForecast: (forecast) => dispatch({ 
      type: ActionTypes.SET_FORECAST, 
      payload: forecast 
    }),
    
    setHistoricalWeather: (data) => dispatch({ 
      type: ActionTypes.SET_HISTORICAL_WEATHER, 
      payload: data 
    }),
    
    setRecommendations: (recommendations) => dispatch({ 
      type: ActionTypes.SET_RECOMMENDATIONS, 
      payload: recommendations 
    }),
    
    setTasks: (tasks) => dispatch({ type: ActionTypes.SET_TASKS, payload: tasks }),
    
    addTask: (task) => dispatch({ type: ActionTypes.ADD_TASK, payload: task }),
    
    updateTask: (task) => dispatch({ 
      type: ActionTypes.UPDATE_TASK, 
      payload: task 
    }),
    
    deleteTask: (taskId) => dispatch({ 
      type: ActionTypes.DELETE_TASK, 
      payload: taskId 
    }),
    
    setLoading: (isLoading) => dispatch({ 
      type: ActionTypes.SET_LOADING, 
      payload: isLoading 
    }),
    
    setError: (error) => dispatch({ type: ActionTypes.SET_ERROR, payload: error }),
    
    clearError: () => dispatch({ type: ActionTypes.CLEAR_ERROR }),
    
    addNotification: (notification) => dispatch({ 
      type: ActionTypes.ADD_NOTIFICATION, 
      payload: notification 
    }),
    
    removeNotification: (notificationId) => dispatch({ 
      type: ActionTypes.REMOVE_NOTIFICATION, 
      payload: notificationId 
    }),
    
    updateUserPreferences: (preferences) => dispatch({ 
      type: ActionTypes.UPDATE_USER_PREFERENCES, 
      payload: preferences 
    }),
  };
  
  return (
    <AppContext.Provider value={{ state, actions }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook for using the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export { AppContextProvider };
export default AppContext;
